import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import FormModal from 'components/shared/modal/FormModal';
import RouteTitle from 'components/shared/route-title/RouteTitle';
import { SlideDown } from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import { getCurrentProject } from 'components/admin/projects/details/store/selectors';
import {
  getShowOpenItemDetailsModal,
  getCritical,
  getCurrentProjectOpenItem,
  getEditOpenItemDetailsModal,
  getOpenItemPdfViewer,
} from 'components/admin/projects/details/project-open-items/store/selectors';
import { getShowEmailModal } from 'components/admin/projects/details/project-email-modal/store/selectors';
import {
  closeOpenItemDetailsModal,
  editOpenItemDetailsModal,
  cancelEditOpenItemDetailsModal,
  setOpenItemForm,
  updateProjectOpenItem,
  deleteProjectOpenItem,
  setOpenItemPdfViewer,
} from 'components/admin/projects/details/project-open-items/store/actions';
import ProjectOpenItemDetails from 'components/admin/projects/details/project-open-items/ProjectOpenItemDetails';
import ProjectOpenItemForm from 'components/admin/projects/details/project-open-items/ProjectOpenItemForm';
import PdfViewer from 'components/shared/pdf-viewer/PdfViewer';
import ProjectEmailModal from 'components/admin/projects/details/project-email-modal/ProjectEmailModal';
import { sendProjectResourceEmails } from 'components/admin/projects/details/store/actions';
import { formatNumber } from 'services/utils/text-util';
import moment from 'moment';

class ProjectOpenItemDetailsModal extends Component {
  static propTypes = {
    onClose: PropTypes.func,
  };

  static defaultProps = {
    onClose: null,
  };

  constructor(props) {
    super(props);

    this.state = { showDeleteConfirmation: false };
  }

  buildEmailModalTitle = () => {
    const { currentProject, openItem } = this.props;
    return `Open Item ${formatNumber(openItem.number)} (Project #${
      currentProject.number
    }: ${currentProject.name})`;
  };

  buildEmailModalSubject = () => {
    const { currentProject, openItem } = this.props;
    return `${currentProject.number}, ${
      currentProject.name
    } - Open Item ${formatNumber(openItem.number)} - ${openItem.description}`;
  };

  buildEmailModalMessage = () => {
    const { currentProject, openItem } = this.props;
    let location = '';
    if (currentProject.building && currentProject.building.location) {
      const buildingLocation = currentProject.building.location;
      location = ` at ${buildingLocation.street_address_1}, ${buildingLocation.city} ${buildingLocation.state}`;
    }

    return `Please find the attached Open Item #${openItem.number} for Project ${currentProject.name}${location}. If you have any questions, please reach out via chat or email.`;
  };

  handleSendEmail = (emailsData) => {
    const { dispatch, openItem } = this.props;
    dispatch(
      sendProjectResourceEmails(openItem.id, 'ProjectOpenItem', emailsData)
    );
  };

  handleOpenItemDetailsModalClose = () => {
    const { onClose, dispatch } = this.props;
    dispatch(closeOpenItemDetailsModal());
    onClose && onClose();
  };

  handleEdit = () => {
    const { openItem, dispatch } = this.props;
    dispatch(
      setOpenItemForm({
        ...openItem,
        projectContributorId: openItem.project_contributor_id || '',
        assigneeId: openItem.assignee_id,
        resolveBy: openItem.resolve_by,
        recipients: openItem.recipients.map((rcpt) => [rcpt.id]),
        documents: openItem.documents.map((doc) => {
          return {
            ...doc,
            url: doc.document.url,
          };
        }),
        images: openItem.images.map((image) => {
          return {
            ...image,
            url: image.image.gallery.url,
          };
        }),
      })
    );
    dispatch(editOpenItemDetailsModal());
  };

  handleEditCancel = () => {
    this.props.dispatch(cancelEditOpenItemDetailsModal());
  };

  handleUpdate = (values) => {
    const { currentProject, openItem, dispatch } = this.props;
    dispatch(updateProjectOpenItem(currentProject.id, openItem.id, values));
  };

  handleDeleteConfirmed = () => {
    const { currentProject, openItem, dispatch } = this.props;
    dispatch(deleteProjectOpenItem(currentProject.id, openItem.id));
  };

  handleDeleteConfirmation = (confirmed) => {
    this.setState({ showDeleteConfirmation: false });
    confirmed && this.handleDeleteConfirmed();
  };

  handleDelete = () => {
    this.setState({ showDeleteConfirmation: true });
  };

  handleRfiPdfViewerClose = () => {
    this.props.dispatch(setOpenItemPdfViewer(false));
  };

  modalTitle = () => {
    const { openItem, critical } = this.props;
    let csiCode = openItem && openItem.csi_code;
    if (!csiCode) {
      csiCode = {};
      csiCode['division'] = '00';
      csiCode['label'] = 'General Contractor';
    }
    let title = openItem ? `Open Item ${openItem.number}` : 'Open Item';
    title = critical ? `Critical ${title}` : title;
    title += csiCode ? ` - ${csiCode.division} ${csiCode.label}` : '';
    return title;
  };

  render() {
    const {
      currentProject,
      openItem,
      showOpenItemDetailsModal,
      editOpenItemDetailsModal,
      openItemPdfViewer,
      showEmailModal,
    } = this.props;
    const { showDeleteConfirmation } = this.state;
    return (
      currentProject &&
      showOpenItemDetailsModal && (
        <FormModal
          className="project-open-item-form-modal"
          show={showOpenItemDetailsModal}
          onClose={this.handleOpenItemDetailsModalClose}
          subTitle={<RouteTitle titles={['Projects', currentProject.name]} />}
          title={this.modalTitle()}
        >
          {editOpenItemDetailsModal ? (
            <SlideDown>
              <ProjectOpenItemForm
                onSave={this.handleUpdate}
                onCancel={this.handleEditCancel}
              />
            </SlideDown>
          ) : (
            <ProjectOpenItemDetails
              onDelete={this.handleDelete}
              onEdit={this.handleEdit}
            />
          )}

          {showDeleteConfirmation && (
            <ConfirmationModal
              show={showDeleteConfirmation}
              message="Are you sure you want to delete this Open Item?"
              onHide={this.handleDeleteConfirmation}
            />
          )}

          {openItemPdfViewer && (
            <PdfViewer
              content={{
                source: `${
                  openItem.pdf_document.document.url
                }?time=${moment().unix()}`,
                type: 'url',
              }}
              show={openItemPdfViewer}
              onClose={this.handleRfiPdfViewerClose}
            />
          )}
          {showEmailModal && (
            <ProjectEmailModal
              title={this.buildEmailModalTitle()}
              handleSendEmail={this.handleSendEmail}
              projectId={currentProject.id}
              initialSubject={this.buildEmailModalSubject()}
              initialMessage={this.buildEmailModalMessage()}
              showHistoryButton
              itemType="ProjectOpenItem"
              itemId={openItem.id}
            />
          )}
        </FormModal>
      )
    );
  }
}

export default connect((state) => {
  return {
    currentProject: getCurrentProject(state),
    openItem: getCurrentProjectOpenItem(state),
    showOpenItemDetailsModal: getShowOpenItemDetailsModal(state),
    editOpenItemDetailsModal: getEditOpenItemDetailsModal(state),
    critical: getCritical(state),
    openItemPdfViewer: getOpenItemPdfViewer(state),
    showEmailModal: getShowEmailModal(state),
  };
})(ProjectOpenItemDetailsModal);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-open-items/ProjectOpenItemDetailsModal.js